import { scry } from "@/lib/scryClient";
// src/components/scan/CardScanner.ts
// Tiny Scryfall + vision helpers used by MyCollection & the scanner UI.

export type ScryfallCard = {
  id: string;
  name: string;
  set: string;                 // 3–5 letter code
  collector_number?: string;
  image_uris?: { normal?: string; large?: string };
  card_faces?: Array<{ image_uris?: { normal?: string; large?: string }, oracle_text?: string }>;
  prices?: { usd?: string | null; usd_foil?: string | null };
  type_line?: string | null;
  colors?: string[] | null;
  color_identity?: string[] | null;
  cmc?: number | null;
  oracle_text?: string | null;
};

const memoryCache = new Map<string, any>();
const put = (k: string, v: any) => { memoryCache.set(k, v); return v; };
const get = (k: string) => memoryCache.get(k);

// ---- single card by id ----
export async function getCardById(id: string, signal?: AbortSignal): Promise<ScryfallCard> {
  const key = `id:${id}`;
  const hit = get(key); if (hit) return hit;
  const res = await scry.getById(id);
  const json = await res.json();
  return put(key, json);
}

// alias used by MyCollection
export const fetchScryfallCardFullById = getCardById;

// ---- by set + collector number ----
export async function scryfallGetBySetNumber(setCode: string, cn: string, signal?: AbortSignal): Promise<ScryfallCard | null> {
  const key = `setnum:${setCode.toLowerCase()}#${cn}`;
  const hit = get(key); if (hit) return hit;
  const q = `set:${setCode} (cn:${cn} OR number:${cn}) unique:prints`;
  const res = await fetch(`https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}`, { signal });
  if (!res.ok) return null;
  const json = await res.json();
  const card = Array.isArray(json.data) && json.data.length ? json.data[0] : null;
  return card ? put(key, card) : null;
}

// ---- search helpers used by scanner / import review ----
export async function searchPrintsByNameExact(name: string, signal?: AbortSignal): Promise<ScryfallCard[]> {
  const key = `prints:"${name}"`;
  const hit = get(key); if (hit) return hit;
  const q = `!"${name}" unique:prints`;
  const data = await scry.search(q, { unique: "prints", order: "released" });
  const arr = Array.isArray(data.data) ? (data.data as ScryfallCard[]) : [];
  put(key, arr);
  return arr;
}

export async function namedFuzzy(name: string, signal?: AbortSignal): Promise<ScryfallCard | null> {
  const key = `fuzzy:${name.toLowerCase()}`;
  const hit = get(key); if (hit) return hit;
  const named = await scry.search(`!"${name}"`, { unique: "prints", order: "released" });
  const base = Array.isArray(named?.data) ? named.data[0] : null;
  return base ? put(key, base) : null;
}

export async function searchBySetAndNumber(name: string, setCode: string, cn: string, signal?: AbortSignal): Promise<ScryfallCard | null> {
  const key = `by-setnum:${setCode.toLowerCase()}#${cn}#${name.toLowerCase()}`;
  const hit = get(key); if (hit) return hit;
  const q = `!"${name}" set:${setCode} (cn:${cn} OR number:${cn}) unique:prints`;
  const res = await fetch(`https://api.scryfall.com/cards/search?q=${encodeURIComponent(q)}`, { signal });
  if (!res.ok) return null;
  const json = await res.json();
  const card = Array.isArray(json.data) && json.data.length ? json.data[0] : null;
  return card ? put(key, card) : null;
}

// ---- image-line text helper (optional, used by OCR flow) ----
export async function detectTextLines(image: CanvasImageSource): Promise<string[]> {
  const TD: any = (window as any).TextDetector;
  if (!TD) return [];
  try {
    const det = new TD();
    const results = await det.detect(image);
    return Array.isArray(results)
      ? results.map((r: any) => r?.rawValue ?? r?.data ?? "").filter(Boolean).map(String)
      : [];
  } catch {
    return [];
  }
}

// small util used by import/preview UIs
export function bestImage(card: ScryfallCard): string | null {
  return (
    card?.image_uris?.normal ||
    card?.image_uris?.large ||
    (Array.isArray(card.card_faces) ? (card.card_faces[0]?.image_uris?.normal || card.card_faces[0]?.image_uris?.large) : null) ||
    null
  );
}


